import { useState } from "react";

import { faArrowLeft, faClose } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { CalendarMonth } from "@mui/icons-material";
import { IconButton, InputAdornment, Stack } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { isValid } from "date-fns";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import styled from "styled-components";

import { useCalendarContext } from "contexts/CalendarContext";
import { Calendar } from "routes/calendar/components/Calendar";
import type { SetSelectArgs } from "routes/calendar/components/Calendar/Calendar";
import { Filters } from "routes/calendar/components/Calendar/components/Filters";
import { BookAppointmentForm } from "routes/calendar/components/Forms/BookAppointmentForm";
import { StyledHeader } from "routes/calendar/components/Forms/Header";
import { useDevice } from "utils/hooks";

interface Props {
  className?: string;
  closeCallback: (value: boolean) => void;
}

export const BookingForm: React.FC<Props> = ({ className, closeCallback }) => {
  const { isMobileOrTablet } = useDevice();
  const { patientId } = useParams<{ patientId?: string }>();
  const { t } = useTranslation();
  const { selectedDate } = useCalendarContext();
  const [step, setStep] = useState<"calendar" | "form">("calendar");
  const [initialDate, setInitialDate] = useState<SetSelectArgs>();
  const [defaultServiceID, setDefaultServiceID] = useState<number | undefined>();

  const isCalendarActive = step === "calendar";
  const isFormActive = step === "form";

  return (
    <Container className={className}>
      <OverflowContainer>
        <Header $isCalendarActive={isCalendarActive}>
          {isCalendarActive ? (
            <Stack direction="row" alignItems="center" spacing={2}>
              <StyledDatePicker
                name="date"
                format="yyyy-MM-dd"
                value={initialDate?.start}
                onChange={input => {
                  if (input && isValid(input)) {
                    setInitialDate({ start: input, end: input });
                  }
                }}
                slots={
                  !isMobileOrTablet
                    ? {
                        openPickerIcon: CalendarMonth,
                      }
                    : undefined
                }
                slotProps={
                  isMobileOrTablet
                    ? {
                        textField: {
                          InputProps: {
                            endAdornment: (
                              <InputAdornment position="end">
                                <CalendarMonth />
                              </InputAdornment>
                            ),
                          },
                        },
                      }
                    : undefined
                }
                disablePast
              />

              <StyledFilters dropdownOrigin="left" />
            </Stack>
          ) : (
            <IconButton onClick={() => setStep("calendar")} size="small">
              <FontAwesomeIcon icon={faArrowLeft} size="sm" />
            </IconButton>
          )}

          {isFormActive && t("booking.forms.header")}

          <IconButton onClick={() => closeCallback(false)} size="small">
            <FontAwesomeIcon icon={faClose} size="sm" />
          </IconButton>
        </Header>

        {isCalendarActive && (
          <StyledCalendar
            customOnSelect={serviceID => {
              setDefaultServiceID(serviceID);
              setStep("form");
            }}
            lockedView="timeGridDay"
            initialDate={initialDate}
            navigateToCalendar
          />
        )}

        {isFormActive && (
          <BookAppointmentForm
            defaultServiceID={defaultServiceID}
            onClose={() => {
              closeCallback(true);
            }}
            selectedDate={selectedDate}
            selectedPatient={patientId}
          />
        )}
      </OverflowContainer>
    </Container>
  );
};

const Container = styled.div`
  width: 400px;
  position: fixed;
  top: 0;
  bottom: 0;
  right: 0;
  z-index: 999;
  background: #fff;

  ${props => props.theme.belowBreakpoint} {
    width: 100vw;
  }
`;

// Safari bug: separate position and overflow to allow children with position fixed to ignore overflow
const OverflowContainer = styled.div`
  height: 100%;
  overflow: hidden auto;
`;

const Header = styled(StyledHeader)<{ $isCalendarActive: boolean }>`
  position: relative;
  z-index: 2;
  ${({ $isCalendarActive }) =>
    $isCalendarActive &&
    `
    padding: 16px;
  `}
`;

const StyledDatePicker = styled(DatePicker)`
  .MuiInputBase-input {
    width: 0;
    padding: 0;
  }

  .MuiInput-underline:after,
  .MuiInput-underline:before {
    display: none;
  }

  .MuiInputAdornment-positionEnd {
    margin-left: 0;
  }

  .MuiButtonBase-root {
    padding: 8px 12px;
    margin-right: 0;
    &:hover {
      background-color: ${props => props.theme.colors.white};
    }
  }

  .MuiInputBase-root {
    height: 38px;
    padding: 0;
  }

  ${props => props.theme.belowBreakpoint} {
    .MuiInputAdornment-root {
      padding: 0 12px;
    }
  }
`;

const StyledFilters = styled(Filters)`
  position: initial !important;
`;

const StyledCalendar = styled(Calendar)`
  margin: 0 !important;

  .fc-header-toolbar {
    justify-content: center;
    margin-bottom: -86px !important;
    margin-left: 62px;
    z-index: 2;

    .fc-toolbar-chunk {
      display: flex;
      gap: 64px;

      .fc-button-primary {
        border: none !important;
        border-radius: 50px;
        padding: 0 8px;
      }
    }
  }

  ${props => props.theme.belowBreakpoint} {
    width: 100vw;

    .fc-header-toolbar {
      margin-bottom: -86px !important;
      margin-left: 32px;
    }
  }
`;
